// What either surface accepts from a file picker, before anything is decoded or
// sent. Both the landing demo and the cabinet's upload page start recognition
// the moment a file arrives, so this is the only gate between the pick and the
// network, and it has to answer from the file's own metadata alone.

// The two types the recognition engine reads. The in-browser resize goes
// through the browser's image decoder as well, so anything else is refused here
// rather than after a round trip.
export const ACCEPTED_IMAGE_TYPE = /^image\/(png|jpeg)$/;

// The largest file a person may hand either surface: 25 MiB, which covers a
// full-resolution photograph from any current phone or camera.
//
// This is a cap on the INPUT, not on what goes over the wire. Both surfaces
// re-encode the picked file in the browser first (see image.ts), so an accepted
// 25 MiB photograph leaves as a few hundred kilobytes. The cap exists because
// decoding an arbitrarily large image costs the visitor's own memory, and
// because a file past it is far more likely to be a mistake than a document.
//
// The fallback path is what sets the ceiling above: when the browser cannot
// decode the image, the original bytes are sent unchanged, so the whole 25 MiB
// can reach the API base64-encoded — a third larger again. Every limit
// downstream is sized for that, and none of them is the one a person meets:
// this message is.
export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;

// Why a picked file cannot be recognised, or null when it can. No decoding, no
// network — the file's type and size are the whole input.
export function rejectImage(file: { type: string; size: number }): string | null {
  if (!ACCEPTED_IMAGE_TYPE.test(file.type)) {
    return "Choose a PNG or JPEG image of a document.";
  }
  if (file.size === 0) {
    return "That file is empty. Choose a PNG or JPEG image of a document.";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    const limit = Math.round(MAX_IMAGE_BYTES / (1024 * 1024));
    return `That image is over ${limit} MB. Choose a smaller file, or photograph the document at a lower resolution.`;
  }
  return null;
}
